import { ReactElement, useState, useMemo } from 'react';
import { useEquipmentCatalog, useManufacturers, useWheelCircumferenceDataFresh } from '../../hooks/useEquipment';
import Table from '../../components/Table';
import { ColumnDef } from '@tanstack/react-table';
import Spinner from '../../components/Spinner';
import SectionHeader from '../../components/SectionHeader';
import AddEquipmentForm from './AddEquipmentForm';
import EditEquipmentForm from './EditEquipmentForm';
import WheelCircumferenceView from './WheelCircumferenceView';
import PencilIcon from '../../icons/PencilIcon';
import { useUser } from 'hooks/useUser';
import Button from '../../components/Button';
import { chipVariants } from '../../components/ChipSelector';

const EquipmentList = (): ReactElement => {
  const [selectedTab, setSelectedTab] = useState('manufacturers');
  const [tyreViewMode, setTyreViewMode] = useState<'table' | 'wheel-circumference'>('table');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<any>(null);
  const { data: equipmentCatalog, isLoading: catalogLoading } = useEquipmentCatalog();
  const { data: manufacturersData, isLoading: manufacturersLoading } = useManufacturers();
  const { data: wheelCircumferenceData } = useWheelCircumferenceDataFresh();
  const [userProfile] = useUser();

  const isLoading = catalogLoading || manufacturersLoading;

  // Extract equipment data from catalog and manufacturers
  const { manufacturers, frames, tyres, frontWheels, rearWheels } = useMemo(() => {
    const defaultReturn = {
      manufacturers: [],
      frames: [],
      tyres: [],
      frontWheels: [],
      rearWheels: [],
    };

    if (!equipmentCatalog) {
      return {
        ...defaultReturn,
        manufacturers: manufacturersData || [],
      };
    }

    return {
      manufacturers: manufacturersData || [],
      frames: equipmentCatalog.frames || [],
      tyres: equipmentCatalog.tyres || [],
      frontWheels: equipmentCatalog.wheels_front || [],
      rearWheels: equipmentCatalog.wheels_rear || [],
    };
  }, [equipmentCatalog, manufacturersData]);

  const handleEditEquipment = (equipment: any) => {
    setEditingEquipment(equipment);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setEditingEquipment(null);
    // Data will be automatically refreshed due to React Query cache invalidation
  };

  // Helper function to create action column for equipment
  const createActionColumn = () => ({
    id: 'actions',
    header: '',
    cell: ({ row }: { row: any }) => {
      // Check if equipment is private (has team_parameters)
      const isPrivate =
        row.original.private || (row.original.team_parameters && row.original.team_parameters.length > 0);

      // Show pencil icon for:
      // 1. Private equipment (existing behavior)
      // 2. Public equipment if user is superuser
      const isSuperuser = userProfile?.superuser === true;
      const shouldShowEdit = isPrivate || (!isPrivate && isSuperuser);

      if (!shouldShowEdit) return null;

      return (
        <button
          onClick={() => handleEditEquipment(row.original)}
          className={`cr-p-1 cr-transition-colors ${
            !isPrivate && isSuperuser
              ? 'cr-text-blue-200 hover:cr-text-gray-700'
              : 'cr-text-gray-400 hover:cr-text-gray-700'
          }`}
          title={!isPrivate && isSuperuser ? 'Edit public equipment (Superuser)' : 'Edit equipment'}
        >
          <PencilIcon size={16} />
        </button>
      );
    },
    size: 40,
  });

  // Define columns for each table type using TanStack Table format
  const manufacturerColumns: ColumnDef<any>[] = useMemo(
    () => [
      createActionColumn(),
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ getValue }) => getValue() || '',
      },
    ],
    [],
  );

  const frameColumns: ColumnDef<any>[] = useMemo(
    () => [
      createActionColumn(),
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'manufacturer',
        header: 'Manufacturer',
        cell: ({ row }) => row.original.manufacturer?.name || 'N/A',
      },
      {
        accessorKey: 'part_number',
        header: 'Part Number',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'designation',
        header: 'Designation',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'size_nominal',
        header: 'Size',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'mass_g',
        header: 'Mass (g)',
        cell: ({ getValue }) => (getValue() ? `${getValue()}` : ''),
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ getValue }) => getValue() || '',
      },
    ],
    [],
  );

  const tyreColumns: ColumnDef<any>[] = useMemo(
    () => [
      createActionColumn(),
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'manufacturer',
        header: 'Manufacturer',
        cell: ({ row }) => row.original.manufacturer?.name || 'N/A',
      },
      {
        accessorKey: 'part_number',
        header: 'Part Number',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'designation',
        header: 'Designation',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'diameter_mm',
        header: 'Diameter (mm)',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'width_mm',
        header: 'Width (mm)',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'mass_g',
        header: 'Mass (g)',
        cell: ({ getValue }) => (getValue() ? `${getValue()}` : ''),
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ getValue }) => getValue() || '',
      },
    ],
    [],
  );

  const wheelColumns: ColumnDef<any>[] = useMemo(
    () => [
      createActionColumn(),
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'manufacturer',
        header: 'Manufacturer',
        cell: ({ row }) => row.original.manufacturer?.name || 'N/A',
      },
      {
        accessorKey: 'part_number',
        header: 'Part Number',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'designation',
        header: 'Designation',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'diameter_mm',
        header: 'Diameter (mm)',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'width_inner_mm',
        header: 'Inner Width (mm)',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'width_outer_mm',
        header: 'Outer Width (mm)',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'depth_mm',
        header: 'Depth (mm)',
        cell: ({ getValue }) => getValue() || '',
      },
      {
        accessorKey: 'mass_g',
        header: 'Mass (g)',
        cell: ({ getValue }) => (getValue() ? `${getValue()}` : ''),
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ getValue }) => getValue() || '',
      },
    ],
    [],
  );

  const tabs = [
    { key: 'manufacturers', label: 'Manufacturers', data: manufacturers, columns: manufacturerColumns },
    { key: 'frames', label: 'Frames', data: frames, columns: frameColumns },
    { key: 'tyres', label: 'Tyres', data: tyres, columns: tyreColumns },
    { key: 'frontWheels', label: 'Front Wheels', data: frontWheels, columns: wheelColumns },
    { key: 'rearWheels', label: 'Rear Wheels', data: rearWheels, columns: wheelColumns },
  ];

  const currentTab = tabs.find((tab) => tab.key === selectedTab) || tabs[0];

  if (isLoading) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
        <Spinner isFetching={isLoading} />
      </div>
    );
  }

  return (
    <div className="cr-space-y-6 cr-p-6">
      {/* Header with bicycle icon */}
      <div className="cr-mb-6">
        <SectionHeader title="Equipment" />
        <p className="cr-text-lg cr-text-cirrusBlue-400">Manage cycling equipment catalog</p>
      </div>

      {/* Tab buttons and Add button */}
      <div className="cr-mb-6 cr-flex cr-items-center cr-justify-between">
        <nav className="cr-flex cr-flex-wrap cr-gap-2" aria-label="Equipment Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key)}
              className={`cr-flex cr-items-center cr-justify-center cr-gap-x-2 cr-rounded-md
                cr-px-4 cr-py-2.5 cr-text-base cr-font-semibold cr-transition-colors cr-duration-200
                ${selectedTab === tab.key ? 'cr-bg-blue-900 cr-text-white' : 'cr-bg-cirrusBlue-100 cr-text-blue-900 hover:cr-bg-gray-350'}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Add Button */}
        <Button
          onClick={() => setShowAddModal(true)}
          variant="secondary"
          className="cr-flex cr-items-center cr-gap-2"
        >
          <span className="cr-text-lg">+</span>
          Add {currentTab.label.slice(0, -1)}
        </Button>
      </div>

      {/* Tyre View Mode Buttons - only show for tyres tab */}
      {selectedTab === 'tyres' && (
        <div className="cr-mb-4 cr-flex cr-gap-2">
          <button
            onClick={() => setTyreViewMode('table')}
            className={chipVariants({ active: tyreViewMode === 'table' })}
          >
            Table
          </button>
          <button
            onClick={() => setTyreViewMode('wheel-circumference')}
            className={chipVariants({ active: tyreViewMode === 'wheel-circumference' })}
          >
            Wheel Circumference
          </button>
        </div>
      )}

      {/* Table - only show when in table mode or not on tyres tab */}
      {(selectedTab !== 'tyres' || tyreViewMode === 'table') && (
        <Table data={currentTab.data} columns={currentTab.columns} isLoading={isLoading} />
      )}

      {/* Wheel Circumference View - only show when tyres tab is selected and wheel circumference mode is active */}
      {selectedTab === 'tyres' && tyreViewMode === 'wheel-circumference' && (
        <WheelCircumferenceView data={wheelCircumferenceData} tyres={tyres} />
      )}

      {/* Add Equipment Form */}
      {showAddModal && (
        <AddEquipmentForm
          equipmentType={selectedTab}
          manufacturers={manufacturers}
          onCancel={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            // Data will be automatically refreshed due to React Query cache invalidation in the mutations
          }}
        />
      )}

      {/* Edit Equipment Form */}
      {showEditModal && editingEquipment && (
        <EditEquipmentForm
          equipmentType={selectedTab}
          equipment={editingEquipment}
          manufacturers={manufacturers}
          onCancel={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default EquipmentList;
